import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Box, TextField, Button, CircularProgress, Slider, IconButton, Tooltip, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SkipPreviousIcon from '@mui/icons-material/SkipPrevious';
import FastRewindIcon from '@mui/icons-material/FastRewind';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import FastForwardIcon from '@mui/icons-material/FastForward';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import traccarApi from '../../../../services/traccarApi';
import useMapStyles from '../../useMapStyles';

const formatTime = (t) => {
  if (!t) return '';
  try {
    return new Date(t).toLocaleString('es-MX', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
  } catch (_) { return t; }
};

const formatSpeed = (knots) => {
  if (knots === undefined || knots === null) return '0.00 km/h';
  return `${(Number(knots) * 1.852).toFixed(2)} km/h`;
};

const POPUP_CSS = `
  .maplibregl-popup { max-width: none !important; }
  .maplibregl-popup-content {
    padding: 0 !important; border-radius: 12px !important;
    box-shadow: 0 4px 20px rgba(0,0,0,0.15) !important; width: 280px;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    overflow: hidden;
  }
  .maplibregl-popup-close-button {
    font-size: 18px; color: #9e9e9e; padding: 4px 8px; line-height: 1;
    right: 4px; top: 4px; z-index: 1; background: transparent;
  }
  .maplibregl-popup-close-button:hover { color: #424242; background: transparent; }
  .maplibregl-popup-tip { border-top-color: white !important; }
  .dp-container { font-family: inherit; }
  .dp-header { padding: 14px 16px 10px 16px; border-bottom: 1px solid #f0f0f0; }
  .dp-name {
    margin: 0; font-size: 14px; font-weight: 600; color: #212121;
    letter-spacing: -0.2px; padding-right: 20px;
  }
  .dp-time {
    margin: 4px 0 0 0; font-size: 12px; color: #9e9e9e; font-weight: 400;
  }
  .dp-body { padding: 12px 16px 14px 16px; display: flex; flex-direction: column; gap: 6px; }
  .dp-row { display: flex; justify-content: space-between; align-items: baseline; gap: 8px; }
  .dp-label { font-size: 12px; color: #9e9e9e; white-space: nowrap; flex-shrink: 0; }
  .dp-value { font-size: 13px; color: #212121; font-weight: 400; text-align: right; word-break: break-word; }
  .dp-divider { height: 1px; background: #f0f0f0; margin: 4px 0; }
  .dp-row-full { flex-direction: column; gap: 2px; align-items: flex-start; }
  .dp-row-full .dp-value { text-align: left; font-size: 12px; color: #616161; }
`;

const buildPopupHTML = (pos, driverName) => {
  const attrs = pos.attributes || {};
  let serviceInfo = {}, vehicleInfo = {};
  try {
    const raw = attrs.service_info;
    serviceInfo = raw ? (typeof raw === 'string' ? JSON.parse(raw) : raw) : {};
  } catch (_) {}
  try {
    const raw = attrs.vehicle_info;
    vehicleInfo = raw ? (typeof raw === 'string' ? JSON.parse(raw) : raw) : {};
  } catch (_) {}

  const name     = driverName || serviceInfo.driver_name || serviceInfo.driver || 'Conductor';
  const time     = formatTime(pos.fixTime || pos.deviceTime || pos.serverTime);
  const service  = serviceInfo.service_code || '—';
  const plate    = vehicleInfo.plate    || attrs.plate    || serviceInfo.plate    || '—';
  const model    = vehicleInfo.model    || attrs.model    || serviceInfo.model    || '—';
  const brand    = vehicleInfo.brand    || attrs.brand    || serviceInfo.brand    || '—';
  const subBrand = vehicleInfo.sub_brand || attrs.submarca || serviceInfo.submarca || '—';
  const address  = pos.address || '';
  const speed    = formatSpeed(pos.speed);

  return `<div class="dp-container">
    <div class="dp-header">
      <p class="dp-name">${name}</p>
      <p class="dp-time">${time}</p>
    </div>
    <div class="dp-body">
      <div class="dp-row"><span class="dp-label">Servicio</span><span class="dp-value">${service}</span></div>
      <div class="dp-row"><span class="dp-label">Placa</span><span class="dp-value">${plate}</span></div>
      <div class="dp-row"><span class="dp-label">Modelo</span><span class="dp-value">${model}</span></div>
      <div class="dp-row"><span class="dp-label">Marca</span><span class="dp-value">${brand}</span></div>
      <div class="dp-row"><span class="dp-label">Submarca</span><span class="dp-value">${subBrand}</span></div>
      ${address ? `<div class="dp-divider"></div><div class="dp-row dp-row-full"><span class="dp-label">Dirección Calle</span><span class="dp-value">${address}</span></div>` : ''}
      <div class="dp-divider"></div>
      <div class="dp-row"><span class="dp-label">Velocidad</span><span class="dp-value">${speed}</span></div>
    </div>
  </div>`;
};

const ReplayMap = ({ positions, currentIndex, driverName, onDotClick, styles: mapStyles }) => {
  const containerRef  = useRef(null);
  const mapRef        = useRef(null);
  const markerRef     = useRef(null);
  const popupRef      = useRef(null);
  const posRef        = useRef(positions);
  const driverRef     = useRef(driverName);
  const onDotRef      = useRef(onDotClick);

  useEffect(() => { posRef.current    = positions;  }, [positions]);
  useEffect(() => { driverRef.current = driverName; }, [driverName]);
  useEffect(() => { onDotRef.current  = onDotClick; }, [onDotClick]);

  const styleUrl = mapStyles?.[0]?.url;

  useEffect(() => {
    if (!styleUrl || mapRef.current) return;
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: styleUrl,
      center: [-99.1332, 19.4326],
      zoom: 12,
      attributionControl: false,
      fadeDuration: 0,
    });
    mapRef.current = map;
    map.addControl(new maplibregl.NavigationControl(), 'top-right');

    const cssId = 'replay-popup-css';
    if (!document.getElementById(cssId)) {
      const s = document.createElement('style');
      s.id = cssId; s.textContent = POPUP_CSS;
      document.head.appendChild(s);
    }

    popupRef.current = new maplibregl.Popup({
      offset: 20, closeButton: true, closeOnClick: false, maxWidth: '320px',
    });

    map.on('load', () => {
      map.addSource('route-src', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
      map.addLayer({ id: 'route-bg', type: 'line', source: 'route-src',
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: { 'line-color': '#ffffff', 'line-width': 7, 'line-opacity': 0.5 } });
      map.addLayer({ id: 'route-line', type: 'line', source: 'route-src',
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: {
          'line-width': 4, 'line-opacity': 0.9,
          'line-color': ['interpolate', ['linear'], ['get', 'speed'],
            0, '#22c55e', 20, '#84cc16', 40, '#eab308', 70, '#f97316', 100, '#ef4444'],
        },
      });

      map.addSource('dots-src', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
      map.addLayer({
        id: 'dots-layer', type: 'circle', source: 'dots-src',
        minzoom: 13,
        paint: {
          'circle-radius': ['interpolate', ['linear'], ['zoom'], 13, 3, 17, 7],
          'circle-color': ['interpolate', ['linear'], ['get', 'speed'],
            0, '#22c55e', 20, '#84cc16', 40, '#eab308', 70, '#f97316', 100, '#ef4444'],
          'circle-stroke-width': 1.5,
          'circle-stroke-color': '#ffffff',
          'circle-opacity': 0.9,
        },
      });

      map.on('click', 'dots-layer', (e) => {
        const feat = e.features?.[0];
        if (!feat) return;
        const idx = feat.properties.index;
        const pos = posRef.current[idx];
        if (!pos) return;
        onDotRef.current?.(idx);
        popupRef.current
          .setLngLat(e.lngLat)
          .setHTML(buildPopupHTML(pos, driverRef.current))
          .addTo(map);
      });

      map.on('mouseenter', 'dots-layer', () => { map.getCanvas().style.cursor = 'pointer'; });
      map.on('mouseleave', 'dots-layer', () => { map.getCanvas().style.cursor = ''; });
    });

    return () => { map.remove(); mapRef.current = null; };
  }, [styleUrl]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !positions.length) return;

    const draw = () => {
      const valid = positions.filter(p => p.latitude && p.longitude);

      const lineFeats = [];
      for (let i = 0; i < valid.length - 1; i++) {
        const a = valid[i], b = valid[i + 1];
        lineFeats.push({
          type: 'Feature',
          properties: { speed: (Number(a.speed) || 0) * 1.852 },
          geometry: { type: 'LineString', coordinates: [
            [Number(a.longitude), Number(a.latitude)],
            [Number(b.longitude), Number(b.latitude)],
          ]},
        });
      }
      map.getSource('route-src')?.setData({ type: 'FeatureCollection', features: lineFeats });

      const dotFeats = valid.map((p, i) => ({
        type: 'Feature',
        properties: { index: i, speed: (Number(p.speed) || 0) * 1.852 },
        geometry: { type: 'Point', coordinates: [Number(p.longitude), Number(p.latitude)] },
      }));
      map.getSource('dots-src')?.setData({ type: 'FeatureCollection', features: dotFeats });

      const coords = valid.map(p => [Number(p.longitude), Number(p.latitude)]);
      const bounds = coords.reduce((b, c) => b.extend(c), new maplibregl.LngLatBounds(coords[0], coords[0]));
      map.fitBounds(bounds, { padding: 60, maxZoom: 16, duration: 800 });
    };

    if (map.isStyleLoaded()) draw();
    else map.once('load', draw);
  }, [positions]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || currentIndex === null || currentIndex === undefined) return;
    const pos = positions[currentIndex];
    if (!pos?.latitude || !pos?.longitude) return;

    const lng = Number(pos.longitude);
    const lat = Number(pos.latitude);

    if (markerRef.current) {
      markerRef.current.setLngLat([lng, lat]);
    } else {
      const el = document.createElement('div');
      el.style.cssText = `
        width:20px;height:20px;border-radius:50%;
        background:#1976d2;border:3px solid white;
        box-shadow:0 2px 8px rgba(0,0,0,0.4);cursor:pointer;
        z-index:10;
      `;
      markerRef.current = new maplibregl.Marker({ element: el })
        .setLngLat([lng, lat])
        .addTo(map);

      el.addEventListener('click', () => {
        const p = posRef.current[currentIndex];
        if (!p) return;
        popupRef.current
          .setLngLat([Number(p.longitude), Number(p.latitude)])
          .setHTML(buildPopupHTML(p, driverRef.current))
          .addTo(map);
      });
    }

    map.easeTo({ center: [lng, lat], duration: 300 });
  }, [currentIndex, positions]);

  return <div ref={containerRef} style={{ width: '100%', height: '100%' }} />;
};

const SPEEDS = [1, 2, 5, 10];

const RouteReplayReport = () => {
  const mapStyles = useMapStyles();

  const [serviceCode,  setServiceCode]  = useState('');
  const [positions,    setPositions]    = useState([]);
  const [loading,      setLoading]      = useState(false);
  const [searched,     setSearched]     = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying,    setIsPlaying]    = useState(false);
  const [playSpeed,    setPlaySpeed]    = useState(1);
  const [driverName,   setDriverName]   = useState('');

  const intervalRef = useRef(null);

  const extractDriverName = useCallback((posArray) => {
    for (const p of posArray) {
      try {
        const raw = p?.attributes?.service_info;
        const info = raw ? (typeof raw === 'string' ? JSON.parse(raw) : raw) : {};
        const found = info.driver_name || info.driver || info.driverName
          || info.conductor || info.nombre_conductor || info.name;
        if (found) return found;
      } catch (_) {}
      const duid = p?.attributes?.driverUniqueId;
      if (duid) return duid;
    }
    return '';
  }, []);

  const fetchPositions = useCallback(async () => {
    const code = serviceCode.trim();
    if (!code) return;
    setLoading(true);
    setIsPlaying(false);
    setPositions([]);
    setCurrentIndex(0);
    setDriverName('');
    clearInterval(intervalRef.current);
    try {
      const to   = new Date();
      const from = new Date(to.getTime() - 2 * 365 * 24 * 60 * 60 * 1000);
      const data = await traccarApi.getReport('route', {
        from: from.toISOString(),
        to:   to.toISOString(),
        serviceCode: code,
      });
      const sorted = (data || [])
        .filter(p => p.latitude && p.longitude)
        .sort((a, b) => new Date(a.fixTime || 0) - new Date(b.fixTime || 0));
      setPositions(sorted);
      setCurrentIndex(0);
      setDriverName(extractDriverName(sorted));
      setSearched(true);
    } catch (err) {
      console.error('[RouteReplayReport] Error:', err);
      setPositions([]);
      setSearched(true);
    } finally {
      setLoading(false);
    }
  }, [serviceCode, extractDriverName]);

  useEffect(() => {
    clearInterval(intervalRef.current);
    if (!isPlaying || !positions.length) return;
    intervalRef.current = setInterval(() => {
      setCurrentIndex(prev => {
        if (prev >= positions.length - 1) { setIsPlaying(false); return prev; }
        return prev + 1;
      });
    }, Math.max(50, 300 / playSpeed));
    return () => clearInterval(intervalRef.current);
  }, [isPlaying, positions.length, playSpeed]);

  const handleReset = () => {
    setPositions([]);
    setSearched(false);
    setIsPlaying(false);
    setCurrentIndex(0);
    setServiceCode('');
    clearInterval(intervalRef.current);
  };

  const currentPos  = positions[currentIndex];
  const currentTime = currentPos ? formatTime(currentPos.fixTime || currentPos.deviceTime) : '';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <Box sx={{ flex: 1, position: 'relative', minHeight: 0 }}>
        <ReplayMap
          positions={positions}
          currentIndex={positions.length ? currentIndex : null}
          driverName={driverName}
          onDotClick={(idx) => { setIsPlaying(false); setCurrentIndex(idx); }}
          styles={mapStyles}
        />
        <Box sx={{
          position: 'absolute', top: 12, left: 12, zIndex: 100,
          bgcolor: 'white', borderRadius: 2,
          boxShadow: '0 2px 12px rgba(0,0,0,0.18)',
          overflow: 'hidden', width: 320,
        }}>
          {positions.length > 0 && (
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 1,
              px: 1.5, py: 1, borderBottom: '1px solid #f0f0f0',
            }}>
              <IconButton size="small" onClick={handleReset} sx={{ color: '#424242' }}>
                <ArrowBackIcon fontSize="small" />
              </IconButton>
              <Typography variant="subtitle2" fontWeight={700} noWrap sx={{ flex: 1 }}>
                {driverName || 'Repetición Ruta'}
              </Typography>
            </Box>
          )}
          <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {!positions.length && (
              <>
                <TextField
                  label="Código de Servicio"
                  value={serviceCode}
                  onChange={e => setServiceCode(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && fetchPositions()}
                  size="small"
                  fullWidth
                  placeholder="TP-XXX"
                  inputProps={{ autoComplete: 'off' }}
                />
                <Button
                  variant="outlined"
                  onClick={fetchPositions}
                  disabled={!serviceCode.trim() || loading}
                  fullWidth
                  sx={{
                    color: '#1a7a4a', borderColor: '#1a7a4a', fontWeight: 600,
                    '&:hover': { bgcolor: '#f0faf4', borderColor: '#155e39' },
                  }}
                >
                  {loading ? <CircularProgress size={16} sx={{ color: '#1a7a4a' }} /> : 'MOSTRAR'}
                </Button>
                {searched && !positions.length && !loading && (
                  <Typography variant="caption" color="text.secondary" textAlign="center">
                    Sin datos para "{serviceCode}"
                  </Typography>
                )}
              </>
            )}
            {positions.length > 0 && (
              <>
                <Slider
                  value={currentIndex}
                  min={0}
                  max={positions.length - 1}
                  onChange={(_, v) => { setIsPlaying(false); setCurrentIndex(v); }}
                  sx={{
                    color: '#1976d2', py: 0.5, mt: -0.5,
                    '& .MuiSlider-thumb': { width: 16, height: 16 },
                  }}
                />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: -1 }}>
                  <Typography variant="caption" color="text.secondary">
                    {currentIndex + 1}/{positions.length}
                  </Typography>
                  <Typography variant="caption" fontWeight={500}>
                    {currentTime}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 0.5 }}>
                  <Tooltip title="Ir al inicio">
                    <IconButton size="small" onClick={() => { setIsPlaying(false); setCurrentIndex(0); }}>
                      <SkipPreviousIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Más lento">
                    <span>
                      <IconButton size="small"
                        onClick={() => setPlaySpeed(s => SPEEDS[Math.max(0, SPEEDS.indexOf(s) - 1)])}
                        disabled={playSpeed === SPEEDS[0]}>
                        <FastRewindIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <IconButton
                    onClick={() => setIsPlaying(p => !p)}
                    disabled={currentIndex >= positions.length - 1 && !isPlaying}
                    sx={{
                      bgcolor: '#1976d2', color: 'white', mx: 0.5,
                      '&:hover': { bgcolor: '#1565c0' },
                      '&.Mui-disabled': { bgcolor: '#bbdefb', color: 'white' },
                    }}
                  >
                    {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
                  </IconButton>
                  <Tooltip title="Más rápido">
                    <span>
                      <IconButton size="small"
                        onClick={() => setPlaySpeed(s => SPEEDS[Math.min(SPEEDS.length - 1, SPEEDS.indexOf(s) + 1)])}
                        disabled={playSpeed === SPEEDS[SPEEDS.length - 1]}>
                        <FastForwardIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Typography variant="caption" sx={{
                    ml: 0.5, px: 1, py: 0.25, borderRadius: 1,
                    bgcolor: '#e3f2fd', color: '#1976d2', fontWeight: 700,
                    minWidth: 32, textAlign: 'center',
                  }}>
                    {playSpeed}x
                  </Typography>
                </Box>
              </>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default RouteReplayReport;
